import { useRef } from "react";
import { format, addWeeks } from "date-fns";
import { sv } from "date-fns/locale";
import Chevron from "@/assets/icons/chevron.svg?react";
import Button from "@/components/Button";
import { StepProps } from "..";
import styles from "../Step.module.scss";
import { gsap } from "gsap";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import { useGSAP } from "@gsap/react";
import { useOnboarding } from "@/hooks/useOnboarding";
import { getTargetWeight } from "@/utils";

gsap.registerPlugin(MotionPathPlugin);

export default function WeightMotivationStep({ onAnswer }: StepProps) {
  const container = useRef<HTMLDivElement>(null);
  const { weight, height, bmi } = useOnboarding();
  const WEEKLY_WEIGHT_LOSS_IN_KG = 1;

  useGSAP(
    () => {
      gsap.to("#marker", {
        motionPath: {
          path: "#weightpath",
          end: 0.85,
        },
        duration: 2,
      });

      gsap.to("#pin", {
        motionPath: {
          path: "#weightpath",
          end: 0.85,
        },
        duration: 0,
        delay: 2,
      });

      gsap.to("#weightReduction", {
        motionPath: {
          path: "#weightpath",
          end: 0.85,
        },
        duration: 0,
        delay: 2.2,
      });

      gsap.fromTo(
        "#pin",
        {
          opacity: 0,
          y: -80,
        },
        {
          opacity: 1,
          y: 20,
          delay: 2,
        }
      );

      gsap.fromTo(
        "#weightReduction",
        {
          opacity: 0,
          y: -110,
        },
        {
          opacity: 1,
          y: -20,
          delay: 2.2,
        }
      );
    },
    { scope: container }
  );

  const targetWeight = height && getTargetWeight(height);
  const weightReduction =
    targetWeight && weight ? Math.ceil(weight - targetWeight) : undefined;
  const renderedWeightReduction =
    weightReduction && `-${weightReduction.toLocaleString()} kg`;
  const dateWeightLossCompleted =
    weightReduction !== undefined &&
    weightReduction > 0 &&
    addWeeks(new Date(), weightReduction / WEEKLY_WEIGHT_LOSS_IN_KG);

  return (
    <div ref={container}>
      <div className={styles.weightMotivationWrapper}>
        {bmi && (
          <div className={styles.currentBmiPill}>
            BMI {bmi.toPrecision(3).replace(".", ",")}
          </div>
        )}
        <svg
          viewBox="0 0 393 171"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            id="weightpath"
            d="M-4 0.5C51 165.5 184 169 397 169"
            stroke="url(#paint0_linear_348_3215)"
            strokeWidth="3"
          />
          {renderedWeightReduction && (
            <>
              <g id="weightReduction">
                <text
                  x={0}
                  y={22}
                  fontFamily="Inter"
                  fontSize="22"
                  fill="green"
                  fontWeight={500}
                  dominantBaseline="middle"
                  textAnchor="middle"
                >
                  {renderedWeightReduction}
                </text>
              </g>
              <g id="pin">
                <rect x="0" y="20" width="1" height="130" fill="#CCC7C3"></rect>
                <rect
                  x="-35"
                  y="0"
                  width="70"
                  height="44"
                  fill="white"
                  rx={20}
                  ry={20}
                ></rect>
                <text
                  x={0}
                  y={24}
                  fontFamily="Inter"
                  fontSize="12"
                  fill="black"
                  fontWeight={500}
                  dominantBaseline="middle"
                  textAnchor="middle"
                >
                  BMI 25
                </text>
              </g>
              <g id="marker">
                <circle
                  cx="0"
                  cy="0"
                  r="28"
                  fill="url(#paint0_radial_1032_129)"
                />
                <circle
                  cx="0"
                  cy="0"
                  r="8"
                  fill="white"
                  stroke="#169248"
                  strokeWidth="3"
                />
                <defs>
                  <radialGradient
                    id="paint0_radial_1032_129"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(28 28) rotate(90) scale(28)"
                  >
                    <stop stopColor="white" stopOpacity="0" />
                    <stop
                      offset="0.645"
                      stopColor="white"
                      stopOpacity="0.1525"
                    />
                    <stop offset="1" stopColor="white" stopOpacity="0.5" />
                  </radialGradient>
                </defs>
              </g>
            </>
          )}

          <defs>
            <linearGradient
              id="paint0_linear_348_3215"
              x1="391"
              y1="169"
              x2="1.73225e-06"
              y2="169"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0.27" stopColor="#169248" stopOpacity={1} />
              <stop offset="0.625" stopColor="#E4AA44" stopOpacity={1} />
              <stop offset="1" stopColor="#C42121" stopOpacity={1} />
            </linearGradient>
          </defs>
        </svg>
      </div>
      <div className={styles.weightReductionInfo}>
        {dateWeightLossCompleted ? (
          <h1>
            Med rätt stöd kan du nå en hälsosammare vikt till{" "}
            {dateWeightLossCompleted
              ? format(dateWeightLossCompleted, "MMMM yyyy", {
                  locale: sv,
                })
              : null}
          </h1>
        ) : null}
        <p>
          Målet kan vara att nå ett BMI på 25
          {targetWeight ? `, vilket motsvarar cirka ${targetWeight} kg` : ""}.
          Resultatet kan variera beroende på individuella förutsättningar
          <sup>1</sup>.
        </p>
      </div>
      <div className={styles.formAction}>
        <Button onClick={() => onAnswer()} iconRight={<Chevron />}>
          Nästa
        </Button>
      </div>
      <div className="disclaimer mt-20">
        <sup>1</sup> Behandlingen innefattar en kombination av
        läkemedelsbehandling, förändrad kost och ökad fysisk aktivitet.
        Individanpassad målsättning görs tillsammans med läkare. För att ta del
        av behandlingen krävs ett BMI över 30, alternativt ett BMI över 27 i
        kombination med andra riskfaktorer såsom högt blodtryck och höga
        blodfetter.
      </div>
    </div>
  );
}
